import React, { useState } from "react";
import { Container, Row, Col, Card, Nav, Button, Offcanvas, Accordion, ListGroup } from "react-bootstrap";
import { Link, Outlet, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import PermissionWrapper from "../components/PermissionWrapper";
import CustomerSupportWidget from "../components/CustomerSupportWidget";

const DashboardPage: React.FC = () => {
  const location = useLocation();
  const menuPermissions = useSelector((state: any) => state.auth.menuPermissions);
  
  const [showSidebar, setShowSidebar] = useState(false);
  
  const menuStructure = [
    {
      title: "User Management",
      icon: "bi-people",
      items: [
        { name: "All Users", path: "/dashboard/manage-all-users", permission: "ManageAllUsersPage", icon: "bi-person" }
      ]
    },
    {
      title: "Group Management", 
      icon: "bi-collection",
      items: [
        { name: "All Groups", path: "/dashboard/manage-all-groups", permission: "ManageAllGroupsPage", icon: "bi-people-fill" },
        { name: "My Group", path: "/dashboard/manage-group", permission: "ManageGroupPage", icon: "bi-person-workspace" }
      ]
    },
    {
      title: "Play Management",
      icon: "bi-stars", 
      items: [
        { name: "All Plays", path: "/dashboard/manage-all-plays", permission: "ManageAllPlaysPage", icon: "bi-play" },
        { name: "All Programs", path: "/dashboard/manage-all-programs", permission: "ManageAllProgramsPage", icon: "bi-collection" },
        { name: "Play Schedules", path: "/dashboard/manage-play-schedules", permission: "ManagePlaySchedulesPage", icon: "bi-calendar2-week" },
        { name: "My Play", path: "/dashboard/manage-play", permission: "ManagePlayPage", icon: "bi-play-circle" },
        { name: "My Program", path: "/dashboard/manage-program", permission: "ManageProgramPage", icon: "bi-collection-play" },
        { name: "My Schedule", path: "/dashboard/manage-schedule", permission: "ManageSchedulePage", icon: "bi-calendar-event" }
      ]
    },
    {
      title: "Content Management",
      icon: "bi-file-earmark-text",
      items: [
        { name: "All Content", path: "/dashboard/manage-all-content", permission: "ManageAllContentPage", icon: "bi-file-text" },
        { name: "YouTube Videos", path: "/dashboard/manage-youtube-videos", permission: "ManageYoutubeVideosPage", icon: "bi-youtube" }
      ]
    },
    {
      title: "Site Settings",
      icon: "bi-gear",
      items: [
        { name: "Site Configuration", path: "/dashboard/site-config", permission: "ManageSiteConfigPage", icon: "bi-sliders" }
      ]
    },
    {
      title: "Booking Management",
      icon: "bi-calendar-check",
      items: [
        { name: "All Bookings", path: "/dashboard/manage-all-bookings", permission: "ManageAllBookingsPage", icon: "bi-calendar3" },
        { name: "All Tickets", path: "/dashboard/manage-all-tickets", permission: "ManageAllTicketsPage", icon: "bi-ticket-perforated" },
        { name: "My Ticket", path: "/dashboard/manage-ticket", permission: "ManageTicketPage", icon: "bi-ticket" },
        { name: "My Bookings", path: "/dashboard/manage-bookings", permission: "ManageBookingPage", icon: "bi-journal-check" },
        { name: "Booking Histories", path: "/dashboard/booking-history", permission: "BookingHistoryPage", icon: "bi-clock-history" }
      ]
    },
    {
      title: "Package Management",
      icon: "bi-box-seam",
      items: [
        { name: "Browse Packages", path: "/dashboard/package-selection", permission: "PackageSelectionPage", icon: "bi-shop" },
        { name: "My Package", path: "/dashboard/group-package", permission: "GroupPackagePage", icon: "bi-box" },
        { name: "Package Stats", path: "/dashboard/package-stats", permission: "PackageStatsPage", icon: "bi-graph-up" },
        { name: "Manage Packages", path: "/dashboard/manage-package", permission: "ManagePackagePage", icon: "bi-gear" },
        { name: "Assign Package", path: "/dashboard/package-assignment", permission: "ManagePackagePage", icon: "bi-box-arrow-in-right" }
      ]
    }
  ];
  
  const allPages = menuStructure.flatMap(section => section.items);

  const hasPermission = (permission: string) => {
    return menuPermissions && menuPermissions.includes(permission);
  };

  const isDashboardHome = location.pathname === "/dashboard";

  const SidebarContent = () => (
    <div className="p-3">
      <h5 className="mb-3">
        <i className="bi bi-speedometer2 me-2"></i>
        Dashboard
      </h5>
      <Accordion className="dashboard-accordion">
        {menuStructure.map((section, sectionIndex) => {
          const hasAnyPermission = section.items.some(item => hasPermission(item.permission));
          if (!hasAnyPermission) return null;
          
          return (
            <Accordion.Item key={sectionIndex} eventKey={sectionIndex.toString()}>
              <Accordion.Header>
                <i className={`${section.icon} me-2`}></i>
                {section.title}
              </Accordion.Header>
              <Accordion.Body className="p-0">
                <ListGroup variant="flush">
                  {section.items.map((item, itemIndex) => 
                    hasPermission(item.permission) && (
                      <ListGroup.Item 
                        key={itemIndex}
                        as={Link}
                        to={item.path}
                        className={`border-0 py-2 px-3 dashboard-submenu-item ${
                          location.pathname === item.path ? "active" : ""
                        }`}
                        onClick={() => setShowSidebar(false)}
                      >
                        <i className={`${item.icon} me-2`}></i>
                        {item.name}
                      </ListGroup.Item>
                    )
                  )}
                </ListGroup>
              </Accordion.Body>
            </Accordion.Item>
          );
        })}
      </Accordion>
    </div>
  );

  return (
    <PermissionWrapper pageName="DashboardPage">
      <Container fluid>
        <Row>
          {/* Desktop Sidebar */}
          <Col md={3} className="dashboard-sidebar min-vh-100 p-0 d-none d-md-block">
            <SidebarContent />
          </Col>
          
          {/* Mobile Sidebar */}
          <Offcanvas show={showSidebar} onHide={() => setShowSidebar(false)} className="d-md-none">
            <Offcanvas.Header closeButton>
              <Offcanvas.Title>
                <i className="bi bi-speedometer2 me-2"></i>
                Dashboard
              </Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body className="p-0">
              <SidebarContent />
            </Offcanvas.Body>
          </Offcanvas>
          
          <Col md={9} className="flex-fill">
            {/* Mobile Menu Button */}
            <div className="d-md-none p-3 border-bottom">
              <Button 
                variant="outline-primary" 
                onClick={() => setShowSidebar(true)}
                className="mb-2"
              >
                <i className="bi bi-list me-2"></i>
                Menu
              </Button>
            </div>
            
            <div className="p-3 p-md-4">
              {isDashboardHome ? (
                <div>
                  <h2 className="mb-4">Welcome to Dashboard</h2>
                  {menuStructure.map((section, sectionIndex) => {
                    const visibleItems = section.items.filter(item => hasPermission(item.permission));
                    if (visibleItems.length === 0) return null;
                    
                    return (
                      <div key={sectionIndex} className="mb-4">
                        <h4 className="mb-3 text-muted">
                          <i className={`${section.icon} me-2`}></i>
                          {section.title}
                        </h4>
                        <Row className="g-3">
                          {visibleItems.map((item, itemIndex) => (
                            <Col xs={12} sm={6} md={4} xl={3} key={itemIndex}>
                              <Card className="h-100 dashboard-card">
                                <Card.Body className="text-center d-flex flex-column justify-content-center">
                                  <i className={`${item.icon} display-4 text-primary mb-3`}></i>
                                  <Card.Title className="h6 mb-3">{item.name}</Card.Title>
                                  <Link to={item.path} className="btn btn-primary btn-sm mt-auto">
                                    Manage
                                  </Link>
                                </Card.Body>
                              </Card>
                            </Col>
                          ))}
                        </Row>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <Outlet />
              )}
            </div>
          </Col>
        </Row>
        
        {/* Customer Support Widget */}
        {/* <CustomerSupportWidget position="bottom-right" /> */}
      </Container>
    </PermissionWrapper>
  );
};

export default DashboardPage;